import L from 'leaflet'
import 'leaflet-draw'
import { App, nextTick, reactive, watch, onMounted, onUnmounted } from 'vue'
import { message } from 'ant-design-vue'
import { OSMConfig, MapTypes } from '/@/constants/index'
import { usePolygonState } from '/@/piniastore/polygonState.js'
import { useflightPlanState } from '/@/piniastore/flightPlanState.js'
// Import Leaflet CSS
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import '../styles/waypoints.css'
import { storeToRefs } from 'pinia'
import { useWaylineState } from '../piniastore/waylineState'
import { useRoute, useRouter } from 'vue-router'
import { useSetWaypointsState } from '../piniastore/setWaypointsState'
import { mapEventBus } from '../utils/mapEventBus'
import { toLeafletCoords, toRCcoords } from '../lib/helperFunctions'
import { useMapState } from '../piniastore/mapState'

// Fix z-index issues with draw controls

const style = document.createElement('style')
style.textContent = `
  .leaflet-draw-toolbar { z-index: 1000 !important; }
  .leaflet-draw-toolbar .leaflet-draw-draw-polyline,
  .leaflet-draw-toolbar .leaflet-draw-draw-polygon,
  .leaflet-draw-toolbar .leaflet-draw-draw-circle,
  .leaflet-draw-toolbar .leaflet-draw-draw-marker { z-index: 1001 !important; }
  .leaflet-draw-toolbar .leaflet-draw-edit-edit,
  .leaflet-draw-toolbar .leaflet-draw-edit-remove { z-index: 1001 !important; }
  .map-type-switcher { position:absolute; top:10px; right:10px; z-index:999; }
  .map-type-switcher .ant-btn { background: #000; border-color: #333; color: #fff; }
  .map-type-switcher .ant-btn:hover { background: #333; border-color: #555; }
  .map-type-switcher .ant-dropdown-menu { background: #000; border: 1px solid #333; }
  .map-type-switcher .ant-dropdown-menu-item { color: #fff; }
  .map-type-switcher .ant-dropdown-menu-item:hover { background: #333; }
  .map-type-dropdown .ant-dropdown-menu { background: #000 !important; border: 1px solid #333 !important; box-shadow: 0 4px 12px rgba(0,0,0,0.5) !important; }
  .map-type-dropdown .ant-dropdown-menu-item { color: #fff !important; }
  .map-type-dropdown .ant-dropdown-menu-item:hover { background: #333 !important; color: #fff !important; }
`
document.head.appendChild(style)

// REMOVE THESE LINES - They're causing circular dependency
// const { clearAllMapLayersonWayline } = useGMapManage()
// const route = useRoute()
// watch(
//   () => route.path,
//   (newPath) => {
//     if (newPath === '/wayline') {
//       console.log('🚪 Leaving area-route, clearing map')
//       clearAllMapLayersonWayline()
//     }
//   }
// )

export function useGMapManage () {
  const state = useMapState()
  const route = useRoute() // Moved inside the function

  // Add route watcher function that can be called from components
  function setupRouteWatcher () {
    let unwatch = null

    try {
      unwatch = watch(
        () => route.path,
        (newPath) => {
          console.log('newPAth:', newPath)
          if (newPath === '/wayline') {
            console.log('🚪 Leaving area-route, clearing map')
            clearAllMapLayersOnWayline()

            // Reset states
            const polygonState = usePolygonState()
            const waylineState = useWaylineState()
            waylineState.setEditWaylineData(null)
            waylineState.setEditMode(false)
            polygonState.setIsFormed(false)
            polygonState.setGeoJsonPolygon(null)
          }
        }
      )
    } catch (error) {
      console.warn('Could not set up route watcher:', error)
    }

    return unwatch
  }

  // Function to destroy the map
  function destroyMap () {
    if (state.map) {
      mapEventBus.emit('mapDestroyed')
      state.map.remove()
      state.map = null
      state.drawControl = null
      state.drawnItems = null
      state.currentTileLayer = null
      state.tileLayers = {}
      state.myLocationMarker = null
      state.myLocationCircle = null
      state.flightLayer = null
      state.routeLine = null
    }
  }

  function enableDrawingMode () {
    if (!state.map) return

    console.log('🔄 Enabling drawing mode for edit mode')

    // Enable map interactions
    state.map.dragging.enable()
    state.map.touchZoom.enable()
    state.map.doubleClickZoom.enable()
    state.map.scrollWheelZoom.enable()

    // Enable draw controls
    if (state.drawControl) {
    // Ensure the edit toolbar is enabled
      const editToolbar = state.drawControl._toolbars?.edit
      if (editToolbar) {
        editToolbar.enable()
        console.log('✅ Draw edit toolbar enabled')
      }
    }

    console.log('✅ Drawing mode fully enabled')
  }

  // Add this to your state and return statement
  state.enableDrawingMode = enableDrawingMode

  // Function to reinitialize the map
  function reinitMap () {
    if (state.mapContainer && state.appInstance) {
      destroyMap()
      initMap(state.mapContainer, state.appInstance)
      locateMe()
    }
  }

  async function initMap (container: string, app: App) {
    try {
      // Store container and app for potential reinitialization
      state.mapContainer = container
      state.appInstance = app

      // Destroy existing map if it exists
      if (state.map) {
        destroyMap()
      }
      // Initialize the map
      state.map = L.map(container).setView(
            OSMConfig.defaultCenter as [number, number],
            OSMConfig.defaultZoom
      )
      // Create tile layers for different map types

      state.tileLayers = {
        voyager: L.tileLayer(MapTypes.VOYAGER.tileServer, {
          attribution: MapTypes.VOYAGER.attribution,
          maxZoom: 19,
        }),
        osm: L.tileLayer(MapTypes.OSM.tileServer, {
          attribution: MapTypes.OSM.attribution,
          maxZoom: 19,
        }),
        satellite: L.tileLayer(MapTypes.SATELLITE.tileServer, {
          attribution: MapTypes.SATELLITE.attribution,
          maxZoom: 19,
        }),
        terrain: L.tileLayer(MapTypes.TERRAIN.tileServer, {
          attribution: MapTypes.TERRAIN.attribution,
          maxZoom: 17,
        }),
        dark: L.tileLayer(MapTypes.DARK.tileServer, {
          attribution: MapTypes.DARK.attribution,
          maxZoom: 19,
        }),
      }
      // Set map as ready and emit event
      state.isMapReady = true

      // Emit map ready event after a short delay to ensure everything is initialized
      setTimeout(() => {
        mapEventBus.emit('mapReady', {
          map: state.map,
          leaflet: L
        })
      }, 100)

      // Add default tile layer (Voyager EN)
      state.currentTileLayer = state.tileLayers.satellite
      state.currentTileLayer.addTo(state.map)

      // Initialize drawing control
      state.drawnItems = new L.FeatureGroup()
      console.log('state drawnItems:', state.drawnItems)
      state.map.addLayer(state.drawnItems)

      state.drawControl = new (L.Control as any).Draw({
        draw: {
          polygon: {
            allowIntersection: false,
            drawError: {
              color: '#e1e100',
              message: "<strong>Oh snap!<strong> you can't draw that!",
            },
            shapeOptions: { color: '#2D8CF0' },
          },
          polyline: { shapeOptions: { color: '#2D8CF0' } },
          circle: { shapeOptions: { color: '#2D8CF0' } },
          marker: {
            icon: L.icon({
              iconUrl: '/@/assets/icons/pin-2d8cf0.svg',
              iconSize: [40, 50],
              iconAnchor: [20, 50],
            }),
          },
        },
        edit: { featureGroup: state.drawnItems, remove: true },
      })

      state.map.addControl(state.drawControl)

      // === Waypoints handling ===
      // Fixed waypoint handling section - replace the waypoint handling code in your initMap function

      // === Fixed Waypoints handling ===
      const waypoints = []
      const waypointMarkers = []
      const routeLine = null
      const distanceMarkers = []

      function initializeExistingWaypoints () {
        const waypointDetailState = useSetWaypointsState()

        if (waypointDetailState.isEditMode && waypointDetailState.waypointsDetails.length > 0) {
          // Clear any existing waypoints
          state.waypoints.length = 0
          state.waypointMarkers.forEach(marker => state.map.removeLayer(marker))
          state.waypointMarkers.length = 0

          // Add existing waypoints from store
          waypointDetailState.waypointsDetails.forEach((wpDetail, index) => {
            const storedLat = wpDetail.waypointAction.latitude
            const storedLng = wpDetail.waypointAction.longitude
            const needsSwap = true

            const leafletLat = needsSwap ? storedLng : storedLat
            const leafletLng = needsSwap ? storedLat : storedLng

            const latlng = L.latLng(leafletLat, leafletLng)
            console.log(`Existing waypoint ${index}: stored[${storedLat}, ${storedLng}] -> leaflet[${leafletLat}, ${leafletLng}]`)

            state.waypoints.push(latlng)

            const label = index === 0 ? 'S' : (index === waypointDetailState.waypointsDetails.length - 1 ? 'E' : (index + 1).toString())

            const marker = L.marker([leafletLat, leafletLng], {
              icon: L.divIcon({
                className: 'waypoint-marker',
                html: `<div style="background:${index === 0 ? 'green' : index === waypointDetailState.waypointsDetails.length - 1 ? 'red' : '#2D8CF0'};
          color:white;border-radius:50%;width:24px;height:24px;
          display:flex;align-items:center;justify-content:center;
          font-size:12px;font-weight:bold;">
          ${label}</div>`,
                iconSize: [24, 24],
                iconAnchor: [12, 12],
              })
            }).addTo(state.map)

            state.waypointMarkers.push(marker)

            marker._waypointData = {
              originalLat: leafletLat,
              originalLng: leafletLng,
              index: index + 1,
              label: label
            }
          })

          updateRoute()

          // ✅ Fit map to the waypoints after loading them
          if (state.waypoints.length > 0) {
            setTimeout(() => {
              fitMapToWaypoints()
            }, 100)
          }
        }
      }

      function enableWaypointMode () {
        if (!state.map) return

        // NEW: Initialize existing waypoints when enabling waypoint mode
        initializeExistingWaypoints()

        // ✅ NEW: Fit map to waypoints if we have any
        if (waypoints.length > 0) {
          setTimeout(() => {
            fitMapToWaypoints()
          }, 150)
        }

        state.map.on('click', addWaypoint)
      }

      function disableWaypointMode () {
        if (!state.map) return
        state.map.off('click', addWaypoint)
      }

      function addWaypoint (e) {
        const waypointDetailState = useSetWaypointsState()

        const startIndex = waypointDetailState.isEditMode
          ? waypointDetailState.waypointsDetails.length
          : state.waypoints.length

        const leafletLat = e.latlng.lat
        const leafletLng = e.latlng.lng

        console.log('🎯 Clicked at Leaflet coords: [lat:', leafletLat, 'lng:', leafletLng, ']')

        const latlng = L.latLng(leafletLat, leafletLng)
        state.waypoints.push(latlng)

        const index = startIndex
        const label = index === 0 ? 'S' : (index === state.waypoints.length ? 'E' : (index + 1).toString())

        const marker = L.marker([leafletLat, leafletLng], {
          icon: L.divIcon({
            className: 'waypoint-marker',
            html: `<div style="background:${index === 0 ? 'green' : index === state.waypoints.length ? 'red' : '#2D8CF0'};
      color:white;border-radius:50%;width:24px;height:24px;
      display:flex;align-items:center;justify-content:center;
      font-size:12px;font-weight:bold;">
      ${label}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          })
        }).addTo(state.map)

        state.waypointMarkers.push(marker)

        marker._waypointData = {
          originalLat: leafletLat,
          originalLng: leafletLng,
          index: index,
          label: label
        }

        addWaypointDetail(leafletLng, leafletLat)
        updateRoute()

        console.log(`📍 Added waypoint ${index} - Leaflet[${leafletLat}, ${leafletLng}] -> Store[lat:${leafletLng}, lng:${leafletLat}]`)
      }

      const waypointDetailState = useSetWaypointsState()

      function addWaypointDetail (rcLat, rcLng) {
        console.log('📝 Adding waypoint detail in RC format:', { rcLat, rcLng })
        const waypointDetailState = useSetWaypointsState()

        // ✅ STORE IN RC FORMAT: latitude field gets RC's longitude value
        //                        longitude field gets RC's latitude value
        // This matches DJI's coordinate system
        const newWaypoint = {
          id: Date.now() + Math.random(),
          waypointAction: {
            speed: waypointDetailState.flightPlanning.speed,
            relativeAltitude: waypointDetailState.flightPlanning.relativeAltitude,
            latitude: rcLat, // This is actually longitude in real-world coords
            longitude: rcLng, // This is actually latitude in real-world coords
            aircraftYaw: waypointDetailState.flightPlanning.aircraftYaw,
            aircraftRotation: '0°',
            waypointType: waypointDetailState.flightPlanning.waypointType,
            gimbalPitch: waypointDetailState.flightPlanning.gimbalControl,
            followRoute: {
              speed: true,
              relativeAltitude: true,
              aircraftYaw: true,
              aircraftRotation: true,
              waypointType: true,
            }
          },
          selectedActions: {
            payloadActions: {
              takeAPhoto: 'takeAPhoto',
              fixedAngleShot: {
                gimbelPitch: 0,
                gimbelYaw: 0,
                aircraftYaw: 0,
                zoom: 0
              },
              panorama: 'panorama',
              startRecording: 'startRecording',
              stopRecording: 'stopRecording',
              gimbalPitchRotation: 0,
              gimbalYaw: 0,
              cameraZoom: 0,
              timedIntervalShot: 0,
              exit: 'exit',
              distanceIntervalShot: 0,
            },
            aircraftActions: {
              hover: 0,
              aircraftRotate: 0
            }
          },
          actionsSelected: [],
        }

        waypointDetailState.waypointsDetails.push(newWaypoint)
        console.log('✅ Waypoint stored in RC format - fields are intentionally swapped')
      }

      function updateRoute () {
        // Remove existing route line
        if (state.waypointsRouteLine) state.map.removeLayer(state.waypointsRouteLine)

        // Create new route line if we have at least 2 waypoints
        if (state.waypoints.length > 1) {
          state.waypointsRouteLine = L.polyline(state.waypoints, { color: '#FF5733' }).addTo(state.map)
        }

        // Remove existing distance markers
        state.distanceMarkers.forEach((m) => state.map.removeLayer(m))
        state.distanceMarkers = []

        const segmentDistances = []

        // Add distance markers for each segment
        for (let i = 1; i < state.waypoints.length; i++) {
          const dist = state.waypoints[i - 1].distanceTo(state.waypoints[i])
          segmentDistances.push(dist)

          const midPoint = L.latLng(
            (state.waypoints[i - 1].lat + state.waypoints[i].lat) / 2,
            (state.waypoints[i - 1].lng + state.waypoints[i].lng) / 2
          )

          // Create distance marker with proper positioning
          const distanceLabel = L.marker(midPoint, {
            icon: L.divIcon({
              className: 'distance-label',
              html: `<div style="background:rgba(255,255,255,0.9);padding:2px 5px;
        border-radius:3px;border:1px solid #ccc;font-size:10px;
        color:#333;text-align:center;">
        ${dist.toFixed(2)} m</div>`,
              iconSize: [null, null],
              iconAnchor: [0, 0],
            }),
            interactive: false,
            zIndexOffset: -1000,
          }).addTo(state.map)

          // Store reference data for zoom updates
          distanceLabel._segmentData = {
            startIndex: i - 1,
            endIndex: i,
            distance: dist
          }

          state.distanceMarkers.push(distanceLabel)
        }

        const waypointDetailState = useSetWaypointsState()
        waypointDetailState.distance = segmentDistances
      }

      function deleteLastWaypoint () {
        if (state.waypoints.length === 0) return

        const waypointDetailState = useSetWaypointsState()

        state.waypoints.pop()

        // Only remove from store if we're not in edit mode or if we added extra waypoints
        if (!waypointDetailState.isEditMode || waypointDetailState.waypointsDetails.length > state.waypoints.length) {
          waypointDetailState.waypointsDetails.pop()
        }

        const marker = state.waypointMarkers.pop()
        if (marker) state.map.removeLayer(marker)

        updateRoute()

        // Update labels for remaining waypoints
        state.waypointMarkers.forEach((marker, index) => {
          const label = index === 0 ? 'S' : (index === state.waypointMarkers.length - 1 ? 'E' : (index + 1).toString())
          const color = index === 0 ? 'green' : (index === state.waypointMarkers.length - 1 ? 'red' : '#2D8CF0')

          marker.setIcon(L.divIcon({
            className: 'waypoint-marker',
            html: `<div style="background:${color};
      color:white;border-radius:50%;width:24px;height:24px;
      display:flex;align-items:center;justify-content:center;
      font-size:12px;font-weight:bold;">
      ${label}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          }))

          // Update stored data
          if (marker._waypointData) {
            marker._waypointData.index = index + 1
            marker._waypointData.label = label
          }
        })
      }

      // Add these functions to your useGMapManage
      function updateEditHandlesOnZoom () {
        if (!state.map || !state.drawControl) return

        const editToolbar = state.drawControl._toolbars?.edit
        if (!editToolbar) return

        const editHandler = editToolbar._modes?.edit?.handler
        if (editHandler && editHandler._markers) {
          Object.values(editHandler._markers).forEach((markerGroup: any) => {
            if (markerGroup.eachLayer) {
              markerGroup.eachLayer((marker: any) => {
                if (marker._latlng) {
                  marker.setLatLng(marker._latlng)
                }
              })
            }
          })
        }
      }

      function refreshEditToolbar () {
        if (!state.drawControl) return

        const editToolbar = state.drawControl._toolbars?.edit
        if (!editToolbar) return

        try {
          editToolbar.disable()
          setTimeout(() => {
            editToolbar.enable()
            console.log('✅ Edit toolbar refreshed')
          }, 50)
        } catch (error) {
          console.warn('Error refreshing edit toolbar:', error)
        }
      }

      function setupZoomHandlers () {
        if (!state.map) return

        state.map.on('zoomend', () => {
          updateMarkersOnZoom()
          updateEditHandlesOnZoom()
          refreshEditToolbar() // Uncomment if above doesn't work
        })

        state.map.on('moveend', () => {
          updateMarkersOnZoom()
          updateEditHandlesOnZoom()
        })
      }

      // Fixed zoom event handler
      function updateMarkersOnZoom () {
        // Update waypoint markers positions
        state.waypointMarkers.forEach((marker, index) => {
          if (marker._waypointData && state.waypoints[index]) {
            const correctLatLng = L.latLng(state.waypoints[index].lat, state.waypoints[index].lng)
            marker.setLatLng(correctLatLng)
          }
        })

        // Update distance markers positions
        state.distanceMarkers.forEach((marker) => {
          if (marker._segmentData) {
            const { startIndex, endIndex } = marker._segmentData
            if (state.waypoints[startIndex] && state.waypoints[endIndex]) {
              const midPoint = L.latLng(
                (state.waypoints[startIndex].lat + state.waypoints[endIndex].lat) / 2,
                (state.waypoints[startIndex].lng + state.waypoints[endIndex].lng) / 2
              )
              marker.setLatLng(midPoint)
            }
          }
        })
      }

      watch(
        () => waypointDetailState.waypointsDetails.map(wp => ({
          storedLat: wp.waypointAction.latitude,
          storedLng: wp.waypointAction.longitude
        })),
        (newCoords) => {
          console.log('🔄 Waypoint coordinates changed in store')

          newCoords.forEach((coord, index) => {
            if (state.waypointMarkers[index] && state.waypoints[index]) {
              const leafletLat = coord.storedLng
              const leafletLng = coord.storedLat

              const newLatLng = L.latLng(leafletLat, leafletLng)

              if (!state.waypoints[index].equals(newLatLng)) {
                state.waypointMarkers[index].setLatLng(newLatLng)
                state.waypoints[index] = newLatLng
                console.log(`✅ Synced waypoint ${index}: Store[${coord.storedLat}, ${coord.storedLng}] -> Leaflet[${leafletLat}, ${leafletLng}]`)
              }
            }
          })

          updateRoute()

          // ✅ Fit map to updated waypoints
          if (state.waypoints.length > 0) {
            setTimeout(() => {
              fitMapToWaypoints()
            }, 100)
          }
        },
        { deep: true }
      )

      state.map.on('draw:created', (e: any) => {
        const { layerType, layer } = e
        console.log('layer:', layer)
        if (layerType === 'polygon') {
          const geoJson = layer.toGeoJSON()

          const polygonState = usePolygonState()
          polygonState.setIsFormed(true)
          polygonState.setGeoJsonPolygon(geoJson)

          // ✅ CRITICAL: Add the new polygon to drawnItems for future editing
          state.drawnItems.addLayer(layer)

          console.log('✅ Polygon created and added to drawnItems for editing')
        }

        if (layerType === 'marker' && useRoute().path === '/set-waypoints') {
          const latlng = layer.getLatLng()
          waypoints.push(latlng)

          // Use the same marker creation method as in addWaypoint
          const index = waypoints.length
          const label = index === 1 ? 'S' : index.toString()

          // Remove the default marker and create our custom one
          state.map.removeLayer(layer)

          const customMarker = L.marker(latlng, {
            icon: L.divIcon({
              className: 'waypoint-marker',
              html: `<div style="background:${index === 1 ? 'green' : '#2D8CF0'};
                color:white;border-radius:50%;width:24px;height:24px;
                display:flex;align-items:center;justify-content:center;
                font-size:12px;font-weight:bold;">
                ${label}</div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            })
          }).addTo(state.map)

          waypointMarkers.push(customMarker)

          // Connect waypoints with polyline
          if (waypoints.length > 1) {
            if (state.routeLine) {
              state.map.removeLayer(state.routeLine)
            }
            state.routeLine = L.polyline(waypoints, { color: '#FF5733' }).addTo(state.map)
          }

          updateRoute()
        }

        // For other layer types (polyline, circle), still add to drawnItems
        if (layerType !== 'marker') {
          state.drawnItems.addLayer(layer)
        }
      })

      const polygonState = usePolygonState()

      // Then in your event handlers, use this stored instance
      state.map.on('draw:edited', (e: any) => {
        const layers = e.layers
        console.log('Polygon layers edited:', layers)

        layers.eachLayer((layer: any) => {
          if (layer instanceof state.leaflet.Polygon) {
            const updatedGeoJson = layer.toGeoJSON()
            console.log('🔄 Polygon edited, updating state:', updatedGeoJson)

            const polygonState = usePolygonState()
            polygonState.setGeoJsonPolygon(updatedGeoJson)
            polygonState.setIsFormed(true)

            console.log('✅ Polygon state updated after edit')
          }
        })
      })

      // Store the functions in state so they can be accessed outside
      state.enableWaypointMode = enableWaypointMode
      state.disableWaypointMode = disableWaypointMode
      state.deleteLastWaypoint = deleteLastWaypoint

      // Attach to global properties
      app.config.globalProperties.$leaflet = state.leaflet
      app.config.globalProperties.$map = state.map
      app.config.globalProperties.$drawControl = state.drawControl
      app.config.globalProperties.$drawnItems = state.drawnItems
      app.config.globalProperties.$switchMapType = switchMapType
      app.config.globalProperties.$locateMe = locateMe
      app.config.globalProperties.$enableWaypointMode = enableWaypointMode
      app.config.globalProperties.$disableWaypointMode = disableWaypointMode
      app.config.globalProperties.$deleteLastWaypoint = deleteLastWaypoint

      locateMe()
      // renderMissionExample()

      const flightPlanStore = useflightPlanState()
      const { flightPlan: flightPlanRef } = storeToRefs(flightPlanStore)
      const flightPlanRemovingState = useflightPlanState()
      watch(
        () => flightPlanRemovingState.isFlightPlanRemoved,
        (newVal) => {
          if (newVal && state.flightLayer) {
            clearFlightPlan()
          }
        }
      )

      function clearFlightPlan () {
        if (state.drawnItems) {
          state.drawnItems.clearLayers()
        }

        if (state.routeLine) {
          state.map.removeLayer(state.routeLine)
          state.routeLine = null
        }

        if (state.flightLayer) {
          state.flightLayer.clearLayers()
          state.map.removeLayer(state.flightLayer)
          state.flightLayer = null
        }

        // const polygonState = usePolygonState()
        // polygonState.setIsFormed(false)
        // polygonState.setGeoJsonPolygon(null)
      }

      watch(
        () => flightPlanRef.value,
        (flightPlanProxy) => {
          try {
            if (!state.map || !flightPlanProxy) {
              return
            }

            // ✅ Extract raw data from Vue Proxy objects
            const flightPlan = JSON.parse(JSON.stringify(flightPlanProxy))

            // Clear previous flightLayer
            if (state.flightLayer) {
              state.flightLayer.clearLayers()
              state.map.removeLayer(state.flightLayer)
            }

            state.flightLayer = new L.FeatureGroup()
            const bounds: any[] = []

            // 1. Draw polygon (extract from Proxy if needed)
            if (flightPlan.flightPlan.polygon) {
              const polygonData = JSON.parse(JSON.stringify(flightPlan.flightPlan.polygon))
              const polygonLayer = L.geoJSON(polygonData, {
                style: { color: 'blue', weight: 2, fillOpacity: 0.1 },
              })
              polygonLayer.addTo(state.flightLayer)
              polygonLayer.getLayers().forEach((layer: any) => {
                const latlngs = layer.getLatLngs()[0]
                bounds.push(...latlngs)
              })
            }

            if (Array.isArray(flightPlan.flightPlan.waypoints)) {
              const latlngs = flightPlan.flightPlan.waypoints.map((wp) => {
                const [lon, lat] = wp.position.geometry.coordinates
                return [lat, lon]
              })

              latlngs.forEach((latlng, index) => {
                const isFirst = index === 0
                const isLast = index === latlngs.length - 1

                // Choose icon or style for start/end
                const marker = L.circleMarker(latlng, {
                  radius: isFirst || isLast ? 6 : 4,
                  color: isFirst ? 'green' : isLast ? 'blue' : 'red',
                  fillColor: 'white',
                  fillOpacity: 1,
                  weight: 2,
                })
                  .bindTooltip(
                    isFirst ? 'Start Point' : isLast ? 'End Point' : `${index}`,
                    { permanent: false }
                  )
                  .addTo(state.flightLayer)

                bounds.push(L.latLng(latlng))
              })
            }

            // 2. Draw waypoints (extract from Proxy if needed)
            if (Array.isArray(flightPlan.flightPlan.waypoints)) {
              flightPlan.flightPlan.waypoints.forEach((wp: any, index: number) => {
                // Extract raw data from waypoint Proxy
                const waypoint = JSON.parse(JSON.stringify(wp))
                const [lon, lat] = waypoint.position.geometry.coordinates

                L.circleMarker([lat, lon], {
                  radius: 4,
                  color: 'green',
                  fillColor: 'white',
                  fillOpacity: 1,
                  weight: 1,
                })
                  .bindTooltip(`${index}`, { permanent: false })
                  .addTo(state.flightLayer)

                bounds.push(L.latLng(lat, lon))
              })
            }

            if (Array.isArray(flightPlan.flightPlan.emergencyLandingSites)) {
              flightPlan.flightPlan.emergencyLandingSites.forEach((site: any) => {
                const siteData = JSON.parse(JSON.stringify(site))
                const [lon, lat] = siteData.geometry.coordinates
                const marker = L.circleMarker([lat, lon], {
                  radius: 5,
                  color: 'orange',
                  fillColor: 'orange',
                  fillOpacity: 0.7,
                }).bindPopup(siteData.properties?.name || 'Emergency Site')

                marker.addTo(state.flightLayer)
                bounds.push(L.latLng(lat, lon))
              })
            }

            if (
              Array.isArray(flightPlan.flightPlan.waypoints) &&
      flightPlan.flightPlan.waypoints.length > 1
            ) {
              const pathCoords = flightPlan.flightPlan.waypoints.map((wp) => {
                const [lon, lat] = wp.position.geometry.coordinates
                return [lat, lon]
              })
              const polyline = L.polyline(pathCoords, {
                color: 'red',
                weight: 2,
                opacity: 1,
              })
              polyline.addTo(state.flightLayer)
              pathCoords.forEach((latlng) => bounds.push(L.latLng(latlng)))
            }

            state.flightLayer.addTo(state.map)

            if (bounds.length) {
              state.map.fitBounds(L.latLngBounds(bounds), { padding: [20, 20] })
            }
          } catch (error) {
            console.error('Error in flight plan rendering:', error)
            console.error('Error details:', error.message, error.stack)
          }
        },
        { deep: true, immediate: true }

        // Watch for route changes to reload the map
      )
      setupZoomHandlers()
    } catch (e) {
      console.error('Failed to load OpenStreetMap:', e)
      state.isMapReady = false
    }
  }

  function switchMapType (mapType: string) {
    if (!state.map || !state.tileLayers[mapType]) {
      console.warn('Map or tile layer not available')
      return
    }
    if (state.currentTileLayer) {
      state.map.removeLayer(state.currentTileLayer)
    }
    state.currentTileLayer = state.tileLayers[mapType]
    state.currentTileLayer.addTo(state.map)
  }

  async function locateMe () {
    const loadingMessage = 'Locating your position... (This may take 10-30 seconds in Electron)'
    message.loading(loadingMessage, 0)

    try {
      const position = await getChromiumLocation()
      const lat = position.coords.latitude
      const lng = position.coords.longitude
      const accuracy = position.coords.accuracy
      const latlng = [lat, lng]

      // Update your map
      updateMapWithLocation(latlng, accuracy)

      message.destroy()

      if (accuracy > 10000) {
        message.warning(`Approximate location found (accuracy: ${Math.round(accuracy)}m)`)
      } else {
        message.success(`Location found! Accuracy: ${Math.round(accuracy)}m`)
      }
    } catch (err) {
      console.error('❌ All location methods failed:', err)
      message.destroy()
      message.error('Could not get location. You can manually click on the map to set your position.')
    }
  }

  function getChromiumLocation () {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        // Provide fallback location
        provideFallbackLocation(resolve)
        return
      }

      // === OPTIMIZED SETTINGS FOR ELECTRON ===
      const options = {
        enableHighAccuracy: false, // MUST be false to avoid GPS timeout
        timeout: 25000, // 25 seconds
        maximumAge: 10 * 60 * 1000 // Use cached location for 10 minutes
      }

      let positionReceived = false
      const timeoutId = setTimeout(() => {
        if (!positionReceived) {
          provideFallbackLocation(resolve)
        }
      }, 26000) // Slightly longer than the geolocation timeout

      navigator.geolocation.getCurrentPosition(
        (position) => {
          positionReceived = true
          clearTimeout(timeoutId)
          resolve(position)
        },
        (error) => {
          positionReceived = true
          clearTimeout(timeoutId)
          console.warn('⚠️ Chromium location error:', error.message)

          // Don't reject - always provide some location
          provideFallbackLocation(resolve)
        },
        options
      )
    })
  }

  function provideFallbackLocation (resolve) {
  // Try IP-based location first
    getIPBasedLocation()
      .then(resolve)
      .catch(() => {
      // Final fallback - default coordinates
        const fallbackPosition = {
          coords: {
            latitude: 28.6139, // Default to Delhi
            longitude: 77.2090,
            accuracy: 50000, // Low accuracy
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null
          },
          timestamp: Date.now()
        }
        resolve(fallbackPosition)
      })
  }

  async function getIPBasedLocation () {
    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        mode: 'cors'
      })

      if (!response.ok) throw new Error('IP API failed')

      const data = await response.json()

      return {
        coords: {
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: 5000, // Moderate accuracy for IP
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null
        },
        timestamp: Date.now()
      }
    } catch (error) {
      console.warn('❌ IP location failed:', error)
      throw error
    }
  }

  function updateMapWithLocation (latlng, accuracy) {
  // Remove existing markers
    if (state.myLocationMarker) {
      state.map.removeLayer(state.myLocationMarker)
    }
    if (state.myLocationCircle) {
      state.map.removeLayer(state.myLocationCircle)
    }

    // Create new marker
    const locationIcon = L.divIcon({
      className: 'my-location-marker',
      html: '<div style="background: #7F00FF; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 10px rgba(45, 140, 240, 0.5);"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    })

    state.myLocationMarker = L.marker(latlng, {
      title: 'My Location',
      icon: locationIcon,
    }).addTo(state.map)

    // Create accuracy circle (only if accuracy is reasonable)
    if (accuracy < 100000) { // Only show circle if accuracy < 100km
      state.myLocationCircle = L.circle(latlng, {
        radius: accuracy,
        color: '#7F00FF',
        weight: 2,
        fillColor: '#2D8CF0',
        fillOpacity: 0.1,
      }).addTo(state.map)
    }

    // Center map on location
    state.map.setView(latlng, Math.max(state.map.getZoom(), 10))
  }

  function globalPropertiesConfig (app: App) {
    initMap('g-container', app)
  }

  const waylineState = useWaylineState()

  function renderMissionExample () {
    const missionData = waylineState.wayline
    const waylineStateStore = useWaylineState()

    if (!state.map) return
    clearAllMapLayers()
    console.log('📍 Rendering mission example, edit mode:', waylineStateStore.isEditMode)

    // Clear old layer if exists
    if (state.flightLayer) {
      state.flightLayer.clearLayers()
      state.map.removeLayer(state.flightLayer)
    }

    // ✅ CRITICAL: Clear drawnItems when in edit mode to prepare for new polygon
    if (waylineStateStore.isEditMode && state.drawnItems) {
      state.drawnItems.clearLayers()
      console.log('✅ Cleared drawnItems for edit mode')
    }

    state.flightLayer = new L.FeatureGroup()
    const bounds = []

    // Check if this is grid planning (has polygon) or waypoint planning
    const hasPolygon = missionData.kml?.Document?.Folder?.Placemark?.Polygon
    const hasWaypoints = missionData.wpml?.Document?.Folder?.Placemark

    if (hasPolygon) {
      console.log('🔷 Rendering grid planning with polygon')

      // Extract and process polygon coordinates
      const kmlPolygonCoords = missionData.kml.Document.Folder.Placemark.Polygon.outerBoundaryIs.LinearRing.coordinates.coordinates
      const polygonPoints = kmlPolygonCoords.split(' ').map(coordStr => {
        const [lon, lat] = coordStr.split(',').map(Number)
        return [lat, lon] // Convert to [lat, lng] for Leaflet
      }).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]))

      if (polygonPoints.length >= 3) {
      // Create the polygon
        const polygon = L.polygon(polygonPoints, {
          color: 'blue',
          weight: 3,
          fillColor: 'blue',
          fillOpacity: 0.1,
          opacity: 0.8
        })

        // ✅ FIX: Add to drawnItems FIRST (this makes it editable)
        state.drawnItems.addLayer(polygon)
        console.log('✅ Polygon added to drawnItems (editable)')

        // Also add to flight layer for display
        polygon.addTo(state.flightLayer)

        // Update polygon state
        const polygonState = usePolygonState()
        const geoJsonPolygon = {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [polygonPoints.map(p => [p[1], p[0]])] // Convert back to [lng, lat] for GeoJSON
          },
          properties: {}
        }
        polygonState.setGeoJsonPolygon(geoJsonPolygon)
        polygonState.setIsFormed(true)
        polygonState.polygonLayer = polygon

        // Add to bounds
        polygonPoints.forEach(point => {
          bounds.push(L.latLng(point[0], point[1]))
        })

        console.log('✅ Polygon rendered with', polygonPoints.length, 'points')
      }

      // Render Waypoints from WPML
      const placemarks = missionData.wpml?.Document?.Folder?.Placemark || []
      const placemarksArray = Array.isArray(placemarks) ? placemarks : [placemarks]

      if (placemarksArray.length > 0) {
        const waypoints = []

        placemarksArray.forEach((pm, i) => {
          if (pm.Point?.coordinates?.coordinates) {
            const [lon, lat] = pm.Point.coordinates.coordinates.split(',').map(Number)
            if (!isNaN(lat) && !isNaN(lon)) {
              waypoints.push([lat, lon])

              const isFirst = i === 0
              const isLast = i === placemarksArray.length - 1
              const label = isFirst ? 'S' : (isLast ? 'E' : (i + 1).toString())
              const color = isFirst ? 'green' : (isLast ? 'red' : '#2D8CF0')

              const marker = L.marker([lat, lon], {
                icon: L.divIcon({
                  className: 'waypoint-marker',
                  html: `<div style="background:${color};color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;">${label}</div>`,
                  iconSize: [24, 24],
                  iconAnchor: [12, 12],
                })
              }).addTo(state.flightLayer)

              marker.bindTooltip(
              `Waypoint ${i}<br>Lat: ${lat.toFixed(6)}<br>Lng: ${lon.toFixed(6)}`,
              { permanent: false }
              )

              bounds.push(L.latLng(lat, lon))
            }
          }
        })

        // Draw connecting polyline
        if (waypoints.length > 1) {
          L.polyline(waypoints, {
            color: '#FF5733',
            weight: 3,
            opacity: 0.8
          }).addTo(state.flightLayer)
        }

        console.log('✅ Waypoints rendered:', waypoints.length)
      }
    } else if (hasWaypoints) {
    // --- Waypoint Planning: Render Only Waypoints ---
      const placemarks = Array.isArray(missionData.wpml.Document.Folder.Placemark)
        ? missionData.wpml.Document.Folder.Placemark
        : [missionData.wpml.Document.Folder.Placemark]

      const waypoints = []

      placemarks.forEach((placemark, index) => {
        if (placemark.Point?.coordinates?.coordinates) {
        // Parse coordinates - format appears to be "longitude,latitude"
          const [lon, lat] = placemark.Point.coordinates.coordinates
            .split(',')
            .map(coord => parseFloat(coord.trim()))

          if (!isNaN(lat) && !isNaN(lon)) {
            waypoints.push([lat, lon])

            // Create marker with different colors for start/end points
            const isFirst = index === 0
            const isLast = index === placemarks.length - 1

            const label = isFirst ? 'S' : (isLast ? 'E' : (index + 1).toString())

            const marker = L.marker([lat, lon], {
              icon: L.divIcon({
                className: 'waypoint-marker',
                html: `<div style="background:${isFirst ? 'green' : isLast ? 'red' : '#2D8CF0'};color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;">${label}</div>`,
                iconSize: [24, 24],
                iconAnchor: [12, 12],
              })
            }).addTo(state.flightLayer)

            // Add tooltip with coordinates
            marker.bindTooltip(
            `Waypoint ${index}<br>Lat: ${lat.toFixed(6)}<br>Lng: ${lon.toFixed(6)}`,
            { permanent: false }
            )

            bounds.push(L.latLng(lat, lon))
          }
        }
      })

      // Draw connecting polyline if we have multiple waypoints
      if (waypoints.length > 1) {
        L.polyline(waypoints, {
          color: '#FF5733',
          weight: 3,
          opacity: 0.8
        }).addTo(state.flightLayer)
      }
    }

    // Add to map and fit bounds
    state.flightLayer.addTo(state.map)
    if (bounds.length > 0) {
      state.map.fitBounds(L.latLngBounds(bounds), { padding: [50, 50], maxZoom: 18 })
    }

    // ✅ IMPORTANT: Enable edit controls after a short delay
    if (waylineStateStore.isEditMode) {
      setTimeout(() => {
        console.log('🔧 Enabling edit controls...')
        console.log('drawnItems layers:', state.drawnItems.getLayers().length)

        if (state.drawControl) {
        // Force enable the edit toolbar
          const editToolbar = state.drawControl._toolbars?.edit
          if (editToolbar) {
            editToolbar._modes.edit.handler.enable()
            console.log('✅ Edit toolbar enabled')
          }
        }
      }, 300)
    }

    console.log('✅ Mission data rendered successfully')
  }

  const waylineUserName = useWaylineState()
  function renderWaypoints () {
    const waylineData = waylineState.wayline

    if (!state.map || !waylineData?.wpml?.Document?.Folder) {
      return
    }

    // Clear existing flight layer
    if (state.flightLayer) {
      state.flightLayer.clearLayers()
      state.map.removeLayer(state.flightLayer)
    }

    state.flightLayer = new L.FeatureGroup()
    const bounds = []
    const waypoints = []

    try {
    // Extract waypoints from WPML data
    // The structure shows multiple Folders, we'll take the first one with Placemarks
      const folders = Array.isArray(waylineData.wpml.Document.Folder)
        ? waylineData.wpml.Document.Folder
        : [waylineData.wpml.Document.Folder]

      // Find the folder with Placemarks
      const folderWithPlacemarks = folders.find(folder => folder.Placemark)

      if (folderWithPlacemarks && folderWithPlacemarks.Placemark) {
        const placemarks = Array.isArray(folderWithPlacemarks.Placemark)
          ? folderWithPlacemarks.Placemark
          : [folderWithPlacemarks.Placemark]

        // Process each waypoint
        placemarks.forEach((placemark, index) => {
          if (placemark.Point?.coordinates?.coordinates) {
          // Parse coordinates - format appears to be "latitude,longitude"
            const [lat, lng] = placemark.Point.coordinates.coordinates
              .split(',')
              .map(coord => parseFloat(coord.trim()))

            if (!isNaN(lat) && !isNaN(lng)) {
              const latlng = lat > lng ? [lng, lat] : [lat, lng]
              waypoints.push(latlng)

              // Create marker with different colors for start/end points
              const isFirst = index === 0
              const isLast = index === placemarks.length - 1

              const label = index === 0 ? 'S' : (index === placemarks.length - 1 ? 'E' : (index + 1).toString())

              const marker = L.marker(latlng, {
                icon: L.divIcon({
                  className: 'waypoint-marker',
                  html: `<div style="background:${index === 0 ? 'green' : index === placemarks.length - 1 ? 'red' : '#2D8CF0'};
                      color:white;border-radius:50%;width:24px;height:24px;
                      display:flex;align-items:center;justify-content:center;
                      font-size:12px;font-weight:bold;">
                      ${label}</div>`,
                  iconSize: [24, 24],
                  iconAnchor: [12, 12],
                })
              }).addTo(state.flightLayer)

              // Add tooltip with coordinates
              marker.bindTooltip(
              `Waypoint ${index}<br>Lat: ${lat.toFixed(6)}<br>Lng: ${lng.toFixed(6)}`,
              { permanent: false }
              )

              bounds.push(L.latLng(latlng))
            }
          }
        })

        // Draw connecting polyline if we have multiple waypoints
        if (waypoints.length > 1) {
          const polyline = L.polyline(waypoints, {
            color: '#FF5733',
            weight: 3,
            opacity: 0.8
          }).addTo(state.flightLayer)
        }

        // Add to map and fit bounds
        state.flightLayer.addTo(state.map)

        if (bounds.length > 0) {
          state.map.fitBounds(L.latLngBounds(bounds), {
            padding: [50, 50],
            maxZoom: 18
          })
        }
      }
    } catch (error) {
      console.error('Error rendering waypoints:', error)
    }
  }

  function fitMapToWaypoints () {
    if (!state.map || !state.waypoints || state.waypoints.length === 0) return

    try {
    // Create bounds from all waypoints
      const bounds = L.latLngBounds(state.waypoints)

      // Fit map to bounds with some padding
      state.map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 18,
        animate: true
      })

      console.log('✅ Map fitted to waypoints bounds')
    } catch (error) {
      console.error('Error fitting map to waypoints:', error)
    }
  }

  function clearAllMapLayers () {
    if (!state.map) return

    console.log('🗑️ Clearing all map layers')

    try {
    // Clear all drawn items
      // if (state.drawnItems) {
      //   state.drawnItems.clearLayers()
      //   console.log('✅ Cleared drawnItems')
      // }

      // Clear flight layer
      // if (state.flightLayer) {
      //   state.flightLayer.clearLayers()
      //   if (state.map.hasLayer(state.flightLayer)) {
      //     state.map.removeLayer(state.flightLayer)
      //   }
      //   state.flightLayer = null
      //   console.log('✅ Cleared flightLayer')
      // }

      // Clear waypoints
      if (state.waypoints) {
        state.waypoints.length = 0
      }

      if (state.waypointMarkers) {
        state.waypointMarkers.forEach(marker => {
          if (marker && state.map.hasLayer(marker)) {
            state.map.removeLayer(marker)
          }
        })
        state.waypointMarkers.length = 0
      }

      // Clear route line
      if (state.waypointsRouteLine) {
        if (state.map.hasLayer(state.waypointsRouteLine)) {
          state.map.removeLayer(state.waypointsRouteLine)
        }
        state.waypointsRouteLine = null
      }

      // Clear distance markers
      if (state.distanceMarkers) {
        state.distanceMarkers.forEach(marker => {
          if (marker && state.map.hasLayer(marker)) {
            state.map.removeLayer(marker)
          }
        })
        state.distanceMarkers.length = 0
      }

      // Clear any global flight layer
      // if (window.currentFlightLayer) {
      //   window.currentFlightLayer.clearLayers()
      //   if (state.map.hasLayer(window.currentFlightLayer)) {
      //     state.map.removeLayer(window.currentFlightLayer)
      //   }
      //   window.currentFlightLayer = null
      // }

      // ✅ CRITICAL: Also remove all layers from the map directly
      // state.map.eachLayer((layer) => {
      // // Keep only the base tile layer and drawnItems
      //   if (layer !== state.currentTileLayer &&
      //     layer !== state.drawnItems) {
      //     state.map.removeLayer(layer)
      //   }
      // })

      // Reset polygon state
      // const polygonState = usePolygonState()
      // polygonState.setIsFormed(false)
      // polygonState.setGeoJsonPolygon(null)
      // polygonState.polygonLayer = null

      // Reset flight plan state
      // const flightPlanState = useflightPlanState()
      // flightPlanState.setFlightPlan(null)

      // Reset wayline state - THIS IS CRITICAL!
      // const waylineState = useWaylineState()
      // waylineState.setWayline(null)
      // waylineState.setEditWaylineData(null)
      // waylineState.setIsEditMode(false)
      // waylineState.deleteId = ''
      // waylineState.filename = ''

      // Reset set waypoints state
      // const setWaypointState = useSetWaypointsState()
      // setWaypointState.resetForNewPlanning()

      // Force map redraw
      // state.map.invalidateSize()

      console.log('✅ All map layers and store data cleared successfully')
    } catch (error) {
      console.error('❌ Error clearing map layers:', error)
    }
  }
  function clearAllMapLayersOnWayline () {
    if (!state.map) return

    console.log('🗑️ Clearing all map layers')

    try {
    // Clear all drawn items
      if (state.drawnItems) {
        state.drawnItems.clearLayers()
        console.log('✅ Cleared drawnItems')
      }

      // Clear flight layer
      if (state.flightLayer) {
        state.flightLayer.clearLayers()
        if (state.map.hasLayer(state.flightLayer)) {
          state.map.removeLayer(state.flightLayer)
        }
        state.flightLayer = null
        console.log('✅ Cleared flightLayer')
      }

      // Clear waypoints
      if (state.waypoints) {
        state.waypoints.length = 0
      }

      if (state.waypointMarkers) {
        state.waypointMarkers.forEach(marker => {
          if (marker && state.map.hasLayer(marker)) {
            state.map.removeLayer(marker)
          }
        })
        state.waypointMarkers.length = 0
      }

      // Clear route line
      if (state.waypointsRouteLine) {
        if (state.map.hasLayer(state.waypointsRouteLine)) {
          state.map.removeLayer(state.waypointsRouteLine)
        }
        state.waypointsRouteLine = null
      }

      // Clear distance markers
      if (state.distanceMarkers) {
        state.distanceMarkers.forEach(marker => {
          if (marker && state.map.hasLayer(marker)) {
            state.map.removeLayer(marker)
          }
        })
        state.distanceMarkers.length = 0
      }

      // Clear any global flight layer
      if (window.currentFlightLayer) {
        window.currentFlightLayer.clearLayers()
        if (state.map.hasLayer(window.currentFlightLayer)) {
          state.map.removeLayer(window.currentFlightLayer)
        }
        window.currentFlightLayer = null
      }

      // ✅ CRITICAL: Also remove all layers from the map directly
      state.map.eachLayer((layer) => {
      // // Keep only the base tile layer and drawnItems
        if (layer !== state.currentTileLayer &&
          layer !== state.drawnItems) {
          state.map.removeLayer(layer)
        }
      })

      // Reset polygon state
      const polygonState = usePolygonState()
      // polygonState.toWayline = true
      polygonState.setIsFormed(false)
      polygonState.setGeoJsonPolygon(null)
      polygonState.polygonLayer = null

      // Reset flight plan state
      const flightPlanState = useflightPlanState()
      flightPlanState.setFlightPlan(null)

      // Reset wayline state - THIS IS CRITICAL!
      const waylineState = useWaylineState()
      waylineState.setWayline(null)
      waylineState.setEditWaylineData(null)
      waylineState.setIsEditMode(false)
      waylineState.deleteId = ''
      waylineState.filename = ''

      // Reset set waypoints state
      const setWaypointState = useSetWaypointsState()
      setWaypointState.resetForNewPlanning()

      // Force map redraw
      state.map.invalidateSize()

      console.log('✅ All map layers and store data cleared successfully')
    } catch (error) {
      console.error('❌ Error clearing map layers:', error)
    }
  }

  // watch(
  //   () => waylineState.wayline,
  //   (newVal) => {
  //     if (newVal) {
  //       console.log('Wayline data changed, rendering mission...', newVal)

  //       // Check if we're in edit mode
  //       const waylineStateStore = useWaylineState()

  //       if (waylineStateStore.isEditMode) {
  //         console.log('Edit mode active, rendering mission for editing')
  //         renderMissionExample()
  //       } else {
  //       // For non-edit mode, use existing logic
  //         if (!newVal?.kml?.Document?.Folder?.Placemark?.Polygon) {
  //           renderWaypoints()
  //         } else {
  //           renderMissionExample()
  //         }
  //       }
  //     }
  //   },
  //   { deep: true, immediate: true }
  // )
  // Replace the existing wayline watcher with this:
  watch(
    () => waylineState.wayline,
    (newVal) => {
    // Don't render if we're clearing or if data is null/empty
      if (!newVal || Object.keys(newVal).length === 0) {
        console.log('🔄 Wayline data cleared, skipping render')
        return
      }

      console.log('Wayline data changed, rendering mission...', newVal)

      // Check if we're in edit mode
      const waylineStateStore = useWaylineState()

      if (waylineStateStore.isEditMode) {
        console.log('Edit mode active, rendering mission for editing')
        renderMissionExample()
      } else {
      // For non-edit mode, use existing logic
        if (!newVal?.kml?.Document?.Folder?.Placemark?.Polygon) {
          renderWaypoints()
        } else {
          renderMissionExample()
        }
      }
    },
    { deep: true, immediate: true }
  )

  const polygonState = usePolygonState()

  // watch(
  //   () => polygonState.polygonLayer,
  //   (newVal) => {
  //     if (newVal) {
  //       console.log('polygonlayer in watch', newVal)
  //       console.log('state:', state.drawnItems)
  //       state.drawnItems.addLayer(newVal)
  //     }
  //   }
  // )

  return {
    globalPropertiesConfig,
    switchMapType,
    locateMe,
    reinitMap,
    destroyMap,
    isMapReady: () => state.isMapReady,
    enableWaypointMode: () => {
      return state.enableWaypointMode?.()
    },
    disableWaypointMode: () => {
      return state.disableWaypointMode?.()
    },
    deleteLastWaypoint: () => {
      if (state.deleteLastWaypoint) {
        state.deleteLastWaypoint()
        return true
      }
      return false
    },
    enableDrawingMode: () => {
      if (state.enableDrawingMode) {
        state.enableDrawingMode()
        return true
      }
      return false
    },
    // ✅ NEW: Add fit to waypoints function
    fitToWaypoints: () => {
      fitMapToWaypoints()
      return true
    },
    clearAllMapLayers: () => {
      clearAllMapLayers()
      return true
    },
    clearAllMapLayersonWayline: () => {
      clearAllMapLayersOnWayline()
      return true
    },
    setupRouteWatcher // Export this function
  }
}
